// Seller intelligence: stored questionnaire responses, the AI acquisition
// assistant, and transcript analysis.
//
// The Need Score itself is computed deterministically on the client and the
// server from `@/lib/seller/need-score`. The model never produces the number —
// it reads the score and the answers and suggests what to say next.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { checkAllowance, recordUsage } from "@/lib/billing/entitlements.functions";
import { modelFor } from "@/lib/ai/routing";
import { NEED_FACTORS, scoreNeed, type NeedAnswers } from "./need-score";

export type JsonValue = string | number | boolean | null | JsonValue[] | { [k: string]: JsonValue };
type Json = { [k: string]: JsonValue };

// ---------------------------------------------------------------------------
// Stored questionnaire responses
// ---------------------------------------------------------------------------

export interface SellerResponseRecord {
  id: string;
  dealRecordId: string | null;
  answers: NeedAnswers;
  needScore: number | null;
  needLabel: string | null;
  factorBreakdown: Json;
  aiAnalysis: Json;
  notes: string | null;
  updatedAt: string;
}

function mapRow(r: Record<string, unknown>): SellerResponseRecord {
  return {
    id: String(r["id"]),
    dealRecordId: (r["deal_record_id"] as string) ?? null,
    answers: (r["answers"] as NeedAnswers) ?? {},
    needScore: (r["need_score"] as number) ?? null,
    needLabel: (r["need_label"] as string) ?? null,
    factorBreakdown: (r["factor_breakdown"] as Json) ?? {},
    aiAnalysis: (r["ai_analysis"] as Json) ?? {},
    notes: (r["notes"] as string) ?? null,
    updatedAt: String(r["updated_at"] ?? ""),
  };
}

export const saveSellerResponse = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      id?: string;
      dealRecordId?: string | null;
      answers: NeedAnswers;
      notes?: string | null;
      aiAnalysis?: Json;
    }) => ({
      id: input?.id ? String(input.id) : undefined,
      dealRecordId: input?.dealRecordId ?? null,
      answers: (input?.answers ?? {}) as NeedAnswers,
      notes: input?.notes ? String(input.notes).slice(0, 8000) : null,
      aiAnalysis: input?.aiAnalysis ?? {},
    }),
  )
  .handler(async ({ data, context }): Promise<SellerResponseRecord | null> => {
    const { supabase, userId } = context;
    // Recompute server-side so the stored score always matches the answers.
    const result = scoreNeed(data.answers);
    const row = {
      user_id: userId,
      deal_record_id: data.dealRecordId,
      answers: data.answers as unknown as Json,
      need_score: result.score,
      need_label: result.band,
      factor_breakdown: {
        contributions: result.contributions,
        coverage: result.coverage,
      } as unknown as Json,
      ai_analysis: data.aiAnalysis,
      notes: data.notes,
    };
    const q = data.id
      ? supabase.from("seller_responses").update(row).eq("id", data.id).eq("user_id", userId)
      : supabase.from("seller_responses").insert(row);
    const { data: saved, error } = await q.select("*").maybeSingle();
    if (error) throw new Error(error.message);
    return saved ? mapRow(saved) : null;
  });

export const listSellerResponses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SellerResponseRecord[]> => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("seller_responses")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(100);
    return (data ?? []).map(mapRow);
  });

// ---------------------------------------------------------------------------
// AI acquisition assistant — recommendations only
// ---------------------------------------------------------------------------

const AssistantSchema = z.object({
  motivation: z.string(),
  timeline: z.string(),
  painPoints: z.array(z.string()),
  objections: z.array(z.object({ objection: z.string(), response: z.string() })),
  nextQuestions: z.array(z.string()),
  counterGuidance: z.string(),
  walkAwayGuidance: z.string(),
  nextBestAction: z.string(),
  reasoning: z.string(),
});

export type SellerAssistantResult = z.infer<typeof AssistantSchema>;

export const runAcquisitionAssistant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      answers: NeedAnswers;
      notes?: string;
      addressText?: string;
      arv?: number;
      repairs?: number;
      maxOffer?: number;
      sellerAsk?: number;
    }) => ({
      answers: (input?.answers ?? {}) as NeedAnswers,
      notes: String(input?.notes ?? "").slice(0, 6000),
      addressText: String(input?.addressText ?? "").slice(0, 300),
      arv: Number(input?.arv) || 0,
      repairs: Number(input?.repairs) || 0,
      maxOffer: Number(input?.maxOffer) || 0,
      sellerAsk: Number(input?.sellerAsk) || 0,
    }),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const apiKey = process.env["LOVABLE_API_KEY"];
    if (!apiKey) throw new Error("AI is not configured for this workspace.");

    const gate = await checkAllowance(supabase, userId, "ai_acquisition_assistant", "ai_reasoning");
    if (!gate.allowed) return { ok: false as const, blockedReason: gate.reason, result: null };

    const score = scoreNeed(data.answers);
    const answered = NEED_FACTORS.filter((f) => data.answers[f.key] != null).map((f) => {
      const level = Math.max(0, Math.min(3, Math.round(data.answers[f.key] as number)));
      return `${f.label}: ${f.levels[level]}`;
    });
    const unknown = score.unknown;

    const { generateStructured } = await import("@/lib/ai-generate.server");

    const prompt = `You are a senior real estate acquisitions manager coaching a wholesaler mid-conversation.

You are given a DETERMINISTIC Need Score that was already computed from the seller's answers. Do not recalculate it, contradict it, or invent facts the seller did not state. Where something is unknown, say it is unknown and turn it into a question to ask.

PROPERTY: ${data.addressText || "not stated"}
ARV (user's number): ${data.arv || "unknown"}
Repairs (user's number): ${data.repairs || "unknown"}
Maximum allowable offer (user's number): ${data.maxOffer || "unknown"}
Seller's asking price: ${data.sellerAsk || "not stated"}

NEED SCORE: ${score.score}/100 (${score.band}), evidence coverage ${score.coverage}%.
WHAT THE SELLER TOLD US:
${answered.length ? answered.map((a) => `- ${a}`).join("\n") : "- nothing recorded yet"}
STILL UNKNOWN:
${unknown.length ? unknown.map((u) => `- ${u}`).join("\n") : "- nothing"}
CALL NOTES:
${data.notes || "none"}

Return:
- motivation: one or two sentences reading their real motivation, grounded only in the above.
- timeline: what their timeline appears to be, or that it is unknown.
- painPoints: up to 5 short phrases.
- objections: up to 4 likely objections, each with a calm, non-pressuring response that leads with fit rather than pressure.
- nextQuestions: 3–6 specific questions that would fill the unknowns above.
- counterGuidance: how to respond if they counter above the maximum offer, in plain language.
- walkAwayGuidance: the point at which this stops being a deal and what to say when walking away politely.
- nextBestAction: one single concrete next step.
- reasoning: two sentences explaining how you reached this, referencing the score factors.

Never guarantee an outcome. Never claim verified data you were not given.`;

    const result = await generateStructured({
      apiKey,
      schema: AssistantSchema,
      prompt,
      temperature: 0.4,
      modelId: modelFor("seller_analysis"),
      label: "Acquisition assistant",
    });

    await recordUsage(supabase, userId, "ai_reasoning", gate.plan, 1, {
      kind: "acquisition_assistant",
    });
    return { ok: true as const, blockedReason: undefined, result };
  });

// ---------------------------------------------------------------------------
// Transcript analysis
// ---------------------------------------------------------------------------

const TranscriptSchema = z.object({
  summary: z.string(),
  motivationSignals: z.array(z.string()),
  timelineSignals: z.array(z.string()),
  objections: z.array(z.string()),
  statedNumbers: z.array(z.string()),
  recommendedFollowUps: z.array(z.string()),
  suggestedAnswers: z.array(
    z.object({ factor: z.string(), level: z.number(), evidence: z.string() }),
  ),
});

export type TranscriptAnalysis = z.infer<typeof TranscriptSchema>;

export const analyzeTranscript = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { title?: string; body: string; dealRecordId?: string | null; save?: boolean }) => {
      const body = String(input?.body ?? "").trim();
      if (body.length < 40)
        throw new Error("Paste a longer transcript — at least a few sentences.");
      return {
        title: String(input?.title ?? "Call transcript").slice(0, 160),
        body: body.slice(0, 40000),
        dealRecordId: input?.dealRecordId ?? null,
        save: input?.save !== false,
      };
    },
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const apiKey = process.env["LOVABLE_API_KEY"];
    if (!apiKey) throw new Error("AI is not configured for this workspace.");

    const gate = await checkAllowance(supabase, userId, "transcript_summary", "ai_reasoning");
    if (!gate.allowed)
      return { ok: false as const, blockedReason: gate.reason, analysis: null, id: null };

    const { generateStructured } = await import("@/lib/ai-generate.server");

    const factorList = NEED_FACTORS.map(
      (f) => `${f.key} (0=${f.levels[0]}, 1=${f.levels[1]}, 2=${f.levels[2]}, 3=${f.levels[3]})`,
    ).join("\n");

    const prompt = `Structure this seller call transcript. Extract only what was actually said. Never infer facts that are not present; leave a list empty instead.

Return:
- summary: 3–5 sentences.
- motivationSignals / timelineSignals / objections / statedNumbers: short quotes or paraphrases from the transcript.
- recommendedFollowUps: concrete next steps for the wholesaler.
- suggestedAnswers: for each need factor below where the transcript gives clear evidence, propose a level 0–3 and quote the evidence. Omit any factor the transcript does not support.

NEED FACTORS:
${factorList}

TRANSCRIPT:
${data.body}`;

    const analysis = await generateStructured({
      apiKey,
      schema: TranscriptSchema,
      prompt,
      temperature: 0.2,
      modelId: modelFor("transcript_structuring"),
      label: "Transcript analysis",
    });

    await recordUsage(supabase, userId, "ai_reasoning", gate.plan, 1, { kind: "transcript" });

    let id: string | null = null;
    if (data.save) {
      const { data: saved } = await supabase
        .from("transcripts")
        .insert({
          user_id: userId,
          deal_record_id: data.dealRecordId,
          title: data.title,
          body: data.body,
          summary: analysis.summary,
          signals: {
            motivation: analysis.motivationSignals,
            timeline: analysis.timelineSignals,
            objections: analysis.objections,
            numbers: analysis.statedNumbers,
          } as Json,
          recommendations: analysis.recommendedFollowUps,
        })
        .select("id")
        .maybeSingle();
      id = (saved?.id as string) ?? null;
    }

    return { ok: true as const, blockedReason: undefined, analysis, id };
  });

export const listTranscripts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("transcripts")
      .select("id, title, summary, created_at, deal_record_id")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);
    return (data ?? []).map((r: Record<string, unknown>) => ({
      id: String(r["id"]),
      title: String(r["title"] ?? "Call transcript"),
      summary: (r["summary"] as string) ?? null,
      createdAt: String(r["created_at"] ?? ""),
      dealRecordId: (r["deal_record_id"] as string) ?? null,
    }));
  });
